import type { CalendarVisibleMonths } from 'components/Common/Calendar/calendarFocusMonth'
import { toCalendarMonthKey } from 'components/Common/Calendar/calendarFocusMonth'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import { getDateFromMonthKey, getLastDayOfMonth } from 'lib/datetime/dateUtils'

/**
 * How many months either side of a visible month still count as "near" an
 * event for experiment exposure. Someone looking at August when the event is in
 * September is part of the measurable population even though nothing is painted
 * on their calendar.
 */
const EXPOSURE_MONTH_TOLERANCE = 1

const MS_PER_DAY = 24 * 60 * 60 * 1000

/** Anchored to `19`/`20` so a numeric suffix that is part of a name survives. */
const YEAR_SUFFIX = /_(?:19|20)\d{2}$/

/** Drops the season year a feed id carries, e.g. `oktoberfest_munich_2026`. */
export function toEventBaseSlug(eventId: string): string {
  return eventId.replace(YEAR_SUFFIX, '')
}

function padDay(day: number): string {
  return `${day}`.padStart(2, '0')
}

function monthKeyToInclusiveIsoRange(monthKey: string): { startDate: string, endDate: string } {
  const monthStart = getDateFromMonthKey(monthKey)
  const lastDay = getLastDayOfMonth(monthStart)
  return {
    startDate: `${monthKey}-01`,
    endDate: `${monthKey}-${padDay(lastDay)}`,
  }
}

function isoRangesOverlap(
  startDate: string,
  endDate: string,
  otherStartDate: string,
  otherEndDate: string,
): boolean {
  return startDate <= otherEndDate && otherStartDate <= endDate
}

function toMonthKeys(visibleMonths?: CalendarVisibleMonths): Array<string> {
  return [visibleMonths?.left, visibleMonths?.right].filter(
    (monthKey): monthKey is string => !!monthKey,
  )
}

/** Offsets a `YYYY-MM` key by whole months, rolling the year over as needed. */
export function offsetMonthKey(monthKey: string, monthOffset: number): string {
  const monthStart = getDateFromMonthKey(monthKey)
  return toCalendarMonthKey(new Date(monthStart.getFullYear(), monthStart.getMonth() + monthOffset))
}

function eventsOverlappingMonthKeys(
  events: ReadonlyArray<SearchCalendarEvent>,
  monthKeys: ReadonlyArray<string>,
): Array<SearchCalendarEvent> {
  if (!monthKeys.length) return []

  return events.filter(event => monthKeys.some(monthKey => {
    const monthRange = monthKeyToInclusiveIsoRange(monthKey)
    return isoRangesOverlap(event.startDate, event.endDate, monthRange.startDate, monthRange.endDate)
  }))
}

export function eventsForPlaceIds(
  events: ReadonlyArray<SearchCalendarEvent>,
  placeIds: ReadonlyArray<string>,
): Array<SearchCalendarEvent> {
  if (!placeIds.length) return []

  const placeIdSet = new Set(placeIds)
  /**
   * `?.` guards against a foreign object reaching this filter — the query this
   * data comes from is unconditional (React's rules of hooks), so tests that
   * blanket-mock `useQuery` without keying on the query itself can hand this
   * function data meant for an entirely different query.
   */
  return events.filter(event => event.searchPlaceIds?.some(placeId => placeIdSet.has(placeId)))
}

/** Events whose dates fall inside a visible month — i.e. what gets painted. */
export function eventsIntersectingMonths(
  events: ReadonlyArray<SearchCalendarEvent>,
  visibleMonths?: CalendarVisibleMonths,
): Array<SearchCalendarEvent> {
  return eventsOverlappingMonthKeys(events, toMonthKeys(visibleMonths))
}

/**
 * Events within {@link EXPOSURE_MONTH_TOLERANCE} months of a visible month.
 *
 * Widens experiment exposure only — never rendering. A user one month either
 * side of an event month is comparable to one looking straight at it, so both
 * belong in the experiment population.
 */
export function eventsNearMonths(
  events: ReadonlyArray<SearchCalendarEvent>,
  visibleMonths?: CalendarVisibleMonths,
): Array<SearchCalendarEvent> {
  const nearMonthKeys = new Set<string>()
  for (const monthKey of toMonthKeys(visibleMonths)) {
    for (let offset = -EXPOSURE_MONTH_TOLERANCE; offset <= EXPOSURE_MONTH_TOLERANCE; offset++) {
      nearMonthKeys.add(offsetMonthKey(monthKey, offset))
    }
  }

  return eventsOverlappingMonthKeys(events, [...nearMonthKeys])
}

export function shouldActivateSearchCalendarEventOverlay(
  events: ReadonlyArray<SearchCalendarEvent>,
  placeIds: ReadonlyArray<string>,
  visibleMonths?: CalendarVisibleMonths,
): boolean {
  return eventsNearMonths(
    eventsExcludingPublicHolidayMarkers(eventsForPlaceIds(events, placeIds)),
    visibleMonths,
  ).length > 0
}

function compareEventsByStart(a: SearchCalendarEvent, b: SearchCalendarEvent): number {
  return a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id)
}

/**
 * Quiet day-cell markers, not the coloured range wash.
 *
 * The recommendations feed tags these as `PUBLIC_HOLIDAY`. `HOLIDAY` is a
 * travel-event type (Golden Week) and still uses the wash.
 */
export function isPublicHolidayMarker(
  event: Pick<SearchCalendarEvent, 'eventTypes'>,
): boolean {
  return event.eventTypes.includes('PUBLIC_HOLIDAY')
}

export function eventsExcludingPublicHolidayMarkers(
  events: ReadonlyArray<SearchCalendarEvent>,
): Array<SearchCalendarEvent> {
  return events.filter(event => !isPublicHolidayMarker(event))
}

export function selectPublicHolidays(
  events: ReadonlyArray<SearchCalendarEvent>,
): Array<SearchCalendarEvent> {
  return events.filter(isPublicHolidayMarker).toSorted(compareEventsByStart)
}

/**
 * Interval-scheduling order: earliest finish first, so the greedy pass below
 * can keep as many non-overlapping events as possible. Id is only a tie-break.
 */
function compareEventsByEnd(a: SearchCalendarEvent, b: SearchCalendarEvent): number {
  return a.endDate.localeCompare(b.endDate)
    || a.startDate.localeCompare(b.startDate)
    || a.id.localeCompare(b.id)
}

/**
 * Every event that can share the visible 2-month block without overlapping
 * another painted range.
 *
 * The overlay can only wash one colour onto a day, so a clash drops the later-
 * finishing event. Earliest finish is chosen because that leaves room for more
 * events; the returned list is then ordered by start so the legend reads
 * left-to-right through the calendar.
 */
export function selectPrimaryEvents(
  events: ReadonlyArray<SearchCalendarEvent>,
): Array<SearchCalendarEvent> {
  const selected: Array<SearchCalendarEvent> = []

  for (const event of events.toSorted(compareEventsByEnd)) {
    const lastSelected = selected[selected.length - 1]
    if (lastSelected && isoRangesOverlap(
      lastSelected.startDate,
      lastSelected.endDate,
      event.startDate,
      event.endDate,
    )) continue

    selected.push(event)
  }

  return selected.toSorted(compareEventsByStart)
}

/** The single earliest event — used for chevron hints and apply analytics. */
export function selectPrimaryEvent(
  events: ReadonlyArray<SearchCalendarEvent>,
): SearchCalendarEvent | undefined {
  return events.toSorted(compareEventsByStart)[0]
}

/** Inclusive count of an event's days that land inside a `YYYY-MM` month. */
export function countHighlightedDatesInMonth(
  event: Pick<SearchCalendarEvent, 'startDate' | 'endDate'>,
  monthKey: string,
): number {
  const monthRange = monthKeyToInclusiveIsoRange(monthKey)
  if (!isoRangesOverlap(event.startDate, event.endDate, monthRange.startDate, monthRange.endDate)) {
    return 0
  }

  const firstIso = event.startDate > monthRange.startDate ? event.startDate : monthRange.startDate
  const lastIso = event.endDate < monthRange.endDate ? event.endDate : monthRange.endDate
  return Math.round((Date.parse(lastIso) - Date.parse(firstIso)) / MS_PER_DAY) + 1
}

/**
 * Events a stay actually spends a night during.
 *
 * A stay occupies the nights `[checkIn, checkOut)` — the room is vacated on the
 * morning of check-out — so the check-out day is exclusive. Treating it as
 * inclusive counts a stay that checks out on an event's first day as attending
 * that event despite spending no night during it, which inflates the conversion
 * numerator. The check-in side stays inclusive because the event's end date is
 * itself a night that can be spent.
 */
/**
 * The one event to preview for a single month, or undefined.
 *
 * Used for the chevron previews, which hint at the month a page would newly
 * reveal — one event per hinted month.
 */
export function eventForMonthKey(
  events: ReadonlyArray<SearchCalendarEvent>,
  monthKey?: string,
): SearchCalendarEvent | undefined {
  if (!monthKey) return undefined

  return selectPrimaryEvent(eventsOverlappingMonthKeys(events, [monthKey]))
}

export function eventsOverlappingStay(
  events: ReadonlyArray<SearchCalendarEvent>,
  checkInIso: string,
  checkOutIso: string,
): Array<SearchCalendarEvent> {
  if (!checkInIso || !checkOutIso) return []

  return events.filter(event => event.startDate < checkOutIso && checkInIso <= event.endDate)
}
